import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchInteriorDebugMetadata,
  fetchInteriorDetections,
  fetchInteriorDetectionsImageObjectUrl,
  fetchInteriorGenerationDebug,
  fetchInteriorGenerationLog,
} from '../api';
import type {
  GenerationLogResponse,
  InteriorDebugMetadataResponse,
  InteriorDetectionsResponse,
  InteriorGenerationDebugCall,
  InteriorGenerationDebugResponse,
} from '../types';
import { createLogger } from '../../utils/logger';

const log = createLogger('InteriorScanDebugModal');

interface InteriorScanDebugModalProps {
  isOpen: boolean;
  jobId: string | null;
  scanId: string | null;
  onClose: () => void;
}

type TabId = 'calls' | 'detections' | 'metadata' | 'log';

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

/**
 * Lightweight full-screen image overlay used by the Crops tab (and the
 * detections backdrop). Click anywhere / Esc to close. Kept intentionally
 * simpler than the ImagesModal `Lightbox` — this is a debug surface.
 */
function ImageOverlay({ url, label, onClose }: { url: string; label: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[58] flex items-center justify-center bg-black/85" onClick={onClose}>
      <img
        src={url}
        alt={label}
        className="max-w-[94vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white backdrop-blur transition-colors"
        aria-label="Close preview"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function MetadataValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-surface-400 italic">—</span>;
  }
  if (typeof value === 'object') {
    return (
      <pre className="text-xs font-mono text-surface-700 bg-surface-50 border border-surface-200 rounded p-2 whitespace-pre-wrap break-words overflow-x-auto">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return <span className="text-sm text-surface-800 break-words">{String(value)}</span>;
}

function KeyValueTable({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <p className="text-sm text-surface-400 italic">No fields.</p>;
  }
  return (
    <dl className="divide-y divide-surface-100 border border-surface-200 rounded-lg overflow-hidden">
      {entries.map(([key, value]) => (
        <div key={key} className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-3 px-3 py-2">
          <dt className="text-xs font-semibold text-surface-500 uppercase tracking-wider break-words">
            {key.replace(/_/g, ' ')}
          </dt>
          <dd className="sm:col-span-2 min-w-0">
            <MetadataValue value={value} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Crops tab (parallel to lightning's Views): per-scene original vs enhanced
// + the enhancement prompt.
// ---------------------------------------------------------------------------

function CropPane({
  title,
  url,
  label,
  onOpen,
}: {
  title: string;
  url: string | null;
  label: string;
  onOpen: (url: string) => void;
}) {
  const [errored, setErrored] = useState(false);
  return (
    <div className="flex-1 min-w-0 flex flex-col">
      <span className="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-2">{title}</span>
      <div className="aspect-[4/3] bg-surface-100 border border-surface-200 rounded-lg flex items-center justify-center overflow-hidden">
        {!url || errored ? (
          <span className="text-surface-400 text-xs italic p-4">
            {url ? 'Failed to load' : 'Not produced'}
          </span>
        ) : (
          <img
            src={url}
            alt={label}
            loading="lazy"
            className="w-full h-full object-contain cursor-zoom-in"
            onClick={() => onOpen(url)}
            onError={() => setErrored(true)}
          />
        )}
      </div>
    </div>
  );
}

/** `invalid` = the model answered but validation rejected the result. */
const GENERATION_STATUS_COLORS: Record<InteriorGenerationDebugCall['status'], string> = {
  succeeded: 'text-success-700',
  failed: 'text-danger-700',
  invalid: 'text-warning-700',
};

/** Prefer the concrete model ID over a shared Python wrapper's class name. */
function generationEngineLabel(call: InteriorGenerationDebugCall): string {
  const configuredModel = call.params.model;
  if (typeof configuredModel === 'string' && configuredModel) {
    return configuredModel;
  }
  if (call.engine === 'gpt_image_2_5_sunburst') {
    return 'gpt-image-2.5-sunburst';
  }
  if (
    call.engine === 'gpt_image_2'
    || call.engine === 'GptImage2Edit'
    || call.engine === 'GptImage2EditFalOnly'
  ) {
    return 'gpt-image-2';
  }
  return call.engine;
}

function GenerationCallsTab({ data }: { data: InteriorGenerationDebugResponse }) {
  const [selectedSequence, setSelectedSequence] = useState<number | null>(
    data.calls[0]?.sequence ?? null,
  );
  const [overlay, setOverlay] = useState<{ url: string; label: string } | null>(null);
  const selected: InteriorGenerationDebugCall | null = useMemo(
    () => data.calls.find((call) => call.sequence === selectedSequence) ?? data.calls[0] ?? null,
    [data.calls, selectedSequence],
  );

  if (data.calls.length === 0) {
    return (
      <div className="p-12 text-center text-surface-400 italic">
        No generation-call artifacts are available for this scan.
      </div>
    );
  }

  const openImage = (url: string, label: string) => setOverlay({ url, label });

  return (
    <div className="relative flex flex-col md:flex-row flex-1 min-h-0">
      <div className="md:w-72 md:shrink-0 md:border-r md:border-b-0 border-b border-surface-200 md:max-h-none max-h-48 overflow-y-auto p-3 space-y-2">
        {data.calls.map((call) => (
          <button
            key={call.sequence}
            type="button"
            onClick={() => setSelectedSequence(call.sequence)}
            className={`w-full p-2.5 rounded-lg border text-left transition-colors ${
              selected?.sequence === call.sequence
                ? 'border-primary-400 bg-primary-50'
                : 'border-surface-200 hover:bg-surface-50'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-surface-800 truncate">
                {call.sequence}. {call.step.replace(/_/g, ' ')}
              </span>
              <span className={`text-[10px] font-semibold uppercase ${
                GENERATION_STATUS_COLORS[call.status] ?? 'text-surface-500'
              }`}>
                {call.status}
              </span>
            </div>
            <p className="mt-1 text-xs text-surface-500">
              {call.stage} · attempt {call.attempt} · {generationEngineLabel(call)}
            </p>
          </button>
        ))}
      </div>

      <div className="flex-1 min-w-0 overflow-y-auto p-4 sm:p-6 space-y-6">
        {selected && (
          <>
            <section>
              <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-3">
                Exact ordered inputs
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {selected.inputs.map((image, index) => (
                  <CropPane
                    key={`${image.role}-${index}`}
                    title={`Image ${index + 1}: ${image.role.replace(/_/g, ' ')}`}
                    url={image.url}
                    label={`${selected.step} input ${index + 1}: ${image.role}`}
                    onOpen={(url) => openImage(url, `${selected.step} input ${index + 1}: ${image.role}`)}
                  />
                ))}
              </div>
            </section>

            <section>
              <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-3">
                Exact model output
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                <CropPane
                  title={selected.stage === 'super_resolution' ? 'After super resolution' : 'Output'}
                  url={selected.outputUrl}
                  label={`${selected.step} output`}
                  onOpen={(url) => openImage(url, `${selected.step} output`)}
                />
                {selected.derivedOutputs.map((image) => (
                  <CropPane
                    key={image.role}
                    title={`Derived: ${image.role.replace(/_/g, ' ')}`}
                    url={image.url}
                    label={`${selected.step} derived ${image.role}`}
                    onOpen={(url) => openImage(url, `${selected.step} derived ${image.role}`)}
                  />
                ))}
              </div>
              {selected.error && (
                <p className="mt-3 text-sm text-danger-700 bg-danger-50 border border-danger-200 rounded-xl p-3">
                  {selected.error}
                </p>
              )}
              {selected.status === 'invalid' && (
                <p className="mt-3 text-sm text-warning-700 bg-warning-50 border border-warning-200 rounded-xl p-3">
                  <span className="font-semibold">Rejected by validation. </span>
                  {selected.validationFeedback ?? 'No feedback was returned by the validator.'}
                </p>
              )}
            </section>

            <section>
              <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-2">
                Request parameters
              </h4>
              <MetadataValue value={selected.params} />
            </section>

            <section>
              <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-2">
                Exact prompt
              </h4>
              <pre className="text-xs leading-relaxed text-surface-700 bg-surface-50 border border-surface-200 rounded-lg p-3 whitespace-pre-wrap break-words font-mono max-h-[55vh] overflow-y-auto">
                {selected.prompt}
              </pre>
            </section>
          </>
        )}
      </div>

      {overlay && (
        <ImageOverlay url={overlay.url} label={overlay.label} onClose={() => setOverlay(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detections tab: boxes + labels + scores overlaid on the panorama.
// ---------------------------------------------------------------------------

// Stable-ish palette keyed by label so the same part keeps its colour.
const DETECTION_COLORS = [
  '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#84cc16', '#06b6d4',
];

function colorForLabel(label: string): string {
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = (hash * 31 + label.charCodeAt(i)) | 0;
  }
  return DETECTION_COLORS[Math.abs(hash) % DETECTION_COLORS.length];
}

// Zoom/pan state for the detections backdrop (image + SVG overlay move
// together so boxes stay aligned). Mirrors the interior Lightbox math.
const DET_MIN_SCALE = 1;
const DET_MAX_SCALE = 8;

interface DetTransform {
  scale: number;
  tx: number;
  ty: number;
}

const DET_IDENTITY: DetTransform = { scale: 1, tx: 0, ty: 0 };

interface DetDrag {
  startX: number;
  startY: number;
  baseTx: number;
  baseTy: number;
  pointerId: number;
}

function DetectionsTab({
  data,
  imageUrl,
}: {
  data: InteriorDetectionsResponse;
  imageUrl: string | null;
}) {
  const { image_width: w, image_height: h, detections } = data;
  // Font/stroke sized in panorama pixel units so they render consistently
  // regardless of the (downscaled) backdrop's display size.
  const fontSize = Math.max(12, Math.round(w / 90));
  const strokeWidth = Math.max(2, Math.round(w / 900));

  const [transform, setTransform] = useState<DetTransform>(DET_IDENTITY);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DetDrag | null>(null);

  // Reset the view whenever the backdrop changes (e.g. modal reopened).
  useEffect(() => {
    setTransform(DET_IDENTITY);
  }, [imageUrl]);

  // Wheel-to-zoom about the container centre (non-passive so we can
  // preventDefault the page scroll).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      setTransform((prev) => {
        const factor = Math.exp(-e.deltaY * 0.0015);
        const nextScale = Math.min(DET_MAX_SCALE, Math.max(DET_MIN_SCALE, prev.scale * factor));
        if (nextScale === DET_MIN_SCALE) return DET_IDENTITY;
        const ratio = nextScale / prev.scale;
        return {
          scale: nextScale,
          tx: (1 - ratio) * (e.clientX - cx) + ratio * prev.tx,
          ty: (1 - ratio) * (e.clientY - cy) + ratio * prev.ty,
        };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (transform.scale <= DET_MIN_SCALE) return;
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseTx: transform.tx,
      baseTy: transform.ty,
      pointerId: e.pointerId,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    e.preventDefault();
    setTransform((prev) => ({
      ...prev,
      tx: d.baseTx + (e.clientX - d.startX),
      ty: d.baseTy + (e.clientY - d.startY),
    }));
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (d && e.pointerId === d.pointerId) dragRef.current = null;
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    setTransform((prev) => {
      if (prev.scale > DET_MIN_SCALE) return DET_IDENTITY;
      const nextScale = 2;
      const ratio = nextScale / prev.scale;
      return {
        scale: nextScale,
        tx: (1 - ratio) * (e.clientX - cx) + ratio * prev.tx,
        ty: (1 - ratio) * (e.clientY - cy) + ratio * prev.ty,
      };
    });
  };

  const isZoomed = transform.scale > DET_MIN_SCALE;
  const cursorClass = isZoomed
    ? dragRef.current
      ? 'cursor-grabbing'
      : 'cursor-grab'
    : 'cursor-zoom-in';

  if (detections.length === 0) {
    return (
      <div className="p-12 text-center text-surface-400 italic">
        No parts were detected for this scan.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold text-surface-800">Parts detections</h4>
        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-surface-100 text-surface-500">
          {detections.length} {detections.length === 1 ? 'box' : 'boxes'}
        </span>
      </div>

      <div
        ref={containerRef}
        className="relative w-full bg-surface-900 rounded-lg overflow-hidden border border-surface-200 select-none"
      >
        {imageUrl ? (
          <div
            className={cursorClass}
            style={{
              transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})`,
              transformOrigin: 'center center',
              transition: dragRef.current ? 'none' : 'transform 80ms ease-out',
              willChange: 'transform',
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onDoubleClick={onDoubleClick}
          >
            <img src={imageUrl} alt="Panorama" draggable={false} className="block w-full h-auto" />
            {/* Overlay sits inside the same transformed wrapper as the
                image so boxes track it 1:1. pointer-events-none lets
                drag/zoom gestures reach the wrapper. */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox={`0 0 ${w} ${h}`}
              preserveAspectRatio="none"
            >
              {detections.map((det, i) => {
                const color = colorForLabel(det.label);
                const labelText = `${det.label.replace(/_/g, ' ')} ${(det.score * 100).toFixed(0)}%`;
                const labelY = det.bbox.y > fontSize * 1.4 ? det.bbox.y - fontSize * 0.4 : det.bbox.y + fontSize;
                return (
                  <g key={`${det.label}-${i}`}>
                    <rect
                      x={det.bbox.x}
                      y={det.bbox.y}
                      width={det.bbox.w}
                      height={det.bbox.h}
                      fill="none"
                      stroke={color}
                      strokeWidth={strokeWidth}
                    />
                    <text
                      x={det.bbox.x + strokeWidth}
                      y={labelY}
                      fontSize={fontSize}
                      fontFamily="monospace"
                      fill={color}
                      stroke="#000"
                      strokeWidth={fontSize / 12}
                      paintOrder="stroke"
                      style={{ fontWeight: 700 }}
                    >
                      {labelText}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        ) : (
          <div className="aspect-[2/1] w-full flex items-center justify-center text-sm text-surface-400">
            Loading panorama…
          </div>
        )}

        {imageUrl && (
          <div className="absolute top-2 right-2 flex items-center gap-2">
            {isZoomed && (
              <button
                type="button"
                onClick={() => setTransform(DET_IDENTITY)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs font-medium backdrop-blur transition-colors"
                title="Reset zoom"
              >
                {Math.round(transform.scale * 100)}% · Reset
              </button>
            )}
            <span className="hidden sm:inline px-2 py-1 rounded-md bg-white/10 text-white/80 text-[10px] backdrop-blur">
              scroll to zoom · drag to pan · double-click to toggle
            </span>
          </div>
        )}
      </div>

      {/* Detection list (sortable-looking table for quick scanning). */}
      <div className="overflow-x-auto">
        <table className="data-table w-full text-xs">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-surface-600 uppercase tracking-wider border-b border-surface-200">
                Label
              </th>
              <th className="px-3 py-2 text-left font-semibold text-surface-600 uppercase tracking-wider border-b border-surface-200">
                Score
              </th>
              <th className="px-3 py-2 text-left font-semibold text-surface-600 uppercase tracking-wider border-b border-surface-200 whitespace-nowrap">
                Box (x, y, w, h)
              </th>
            </tr>
          </thead>
          <tbody>
            {detections.map((det, i) => (
              <tr key={`${det.label}-row-${i}`} className="border-b border-surface-100 hover:bg-surface-50">
                <td data-label="Label" className="px-3 py-1.5 text-surface-700 align-top">
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="inline-block w-3 h-3 rounded-sm shrink-0"
                      style={{ backgroundColor: colorForLabel(det.label) }}
                    />
                    <span className="capitalize">{det.label.replace(/_/g, ' ')}</span>
                  </span>
                </td>
                <td data-label="Score" className="px-3 py-1.5 text-surface-700 align-top font-mono">
                  {(det.score * 100).toFixed(1)}%
                </td>
                <td data-label="Box" className="px-3 py-1.5 text-surface-700 align-top font-mono whitespace-nowrap">
                  {Math.round(det.bbox.x)}, {Math.round(det.bbox.y)}, {Math.round(det.bbox.w)}, {Math.round(det.bbox.h)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Metadata tab
// ---------------------------------------------------------------------------

function MetadataTab({ data }: { data: InteriorDebugMetadataResponse }) {
  const hasRequest = !!data.request_params && Object.keys(data.request_params).length > 0;
  const hasVehicle = !!data.vehicle_metadata && Object.keys(data.vehicle_metadata).length > 0;

  if (!hasRequest && !hasVehicle) {
    return (
      <div className="p-12 text-center text-surface-400 italic">
        No metadata available for this scan.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-8">
      <section>
        <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-3">
          Request parameters
        </h4>
        {hasRequest ? (
          <KeyValueTable data={data.request_params as Record<string, unknown>} />
        ) : (
          <p className="text-sm text-surface-400 italic">No request parameters recorded.</p>
        )}
      </section>

      <section>
        <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-3">
          Vehicle metadata
        </h4>
        {hasVehicle ? (
          <KeyValueTable data={data.vehicle_metadata as Record<string, unknown>} />
        ) : (
          <p className="text-sm text-surface-400 italic">No vehicle metadata recorded.</p>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generation log tab
// ---------------------------------------------------------------------------

function LogTab({ data }: { data: GenerationLogResponse }) {
  if (data.columns.length === 0 || data.rows.length === 0) {
    return (
      <div className="p-12 text-center text-surface-400 italic">The generation log is empty.</div>
    );
  }
  return (
    <div className="p-4 sm:p-6">
      <table className="data-table w-full text-xs">
        <thead>
          <tr>
            {data.columns.map((col) => (
              <th
                key={col}
                className="px-3 py-2 text-left font-semibold text-surface-600 uppercase tracking-wider whitespace-nowrap border-b border-surface-200"
              >
                {col.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, idx) => (
            <tr key={idx} className="border-b border-surface-100 hover:bg-surface-50">
              {data.columns.map((col) => (
                <td
                  key={col}
                  data-label={col.replace(/_/g, ' ')}
                  className="px-3 py-1.5 text-surface-700 align-top font-mono whitespace-nowrap"
                >
                  {row[col] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal shell
// ---------------------------------------------------------------------------

const TABS: { id: TabId; label: string }[] = [
  { id: 'calls', label: 'Generation Calls' },
  { id: 'detections', label: 'Detections' },
  { id: 'metadata', label: 'Metadata' },
  { id: 'log', label: 'Generation Log' },
];

export function InteriorScanDebugModal({ isOpen, jobId, scanId, onClose }: InteriorScanDebugModalProps) {
  const [tab, setTab] = useState<TabId>('calls');

  const [callsData, setCallsData] = useState<InteriorGenerationDebugResponse | null>(null);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsError, setCallsError] = useState<string | null>(null);
  const callsFetchedRef = useRef(false);

  const [detectionsData, setDetectionsData] = useState<InteriorDetectionsResponse | null>(null);
  const [detectionsImageUrl, setDetectionsImageUrl] = useState<string | null>(null);
  const [detectionsLoading, setDetectionsLoading] = useState(false);
  const [detectionsError, setDetectionsError] = useState<string | null>(null);
  const detectionsFetchedRef = useRef(false);
  // Holds the live detections backdrop object URL so we can revoke it
  // exactly once — on modal close / unmount — rather than on every tab
  // switch (which would leave the cached <img> pointing at a revoked
  // blob URL when the user returns to the Detections tab).
  const detectionsImageUrlRef = useRef<string | null>(null);

  const [metadataData, setMetadataData] = useState<InteriorDebugMetadataResponse | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const metadataFetchedRef = useRef(false);

  const [logData, setLogData] = useState<GenerationLogResponse | null>(null);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const logFetchedRef = useRef(false);

  // Exact generation calls: lazy on first visit.
  useEffect(() => {
    if (!isOpen || !jobId || tab !== 'calls') return;
    if (callsFetchedRef.current) return;
    callsFetchedRef.current = true;
    let cancelled = false;
    setCallsLoading(true);
    setCallsError(null);
    fetchInteriorGenerationDebug(jobId)
      .then((resp) => {
        if (!cancelled) setCallsData(resp);
      })
      .catch((err) => {
        if (!cancelled) {
          log.error(`Failed to fetch generation calls for job ${jobId}:`, err);
          setCallsError(err instanceof Error ? err.message : 'Failed to load generation calls');
        }
      })
      .finally(() => {
        if (!cancelled) setCallsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, tab]);

  // Detections + backdrop: lazy on first visit to the tab.
  useEffect(() => {
    if (!isOpen || !jobId || tab !== 'detections') return;
    if (detectionsFetchedRef.current) return;
    detectionsFetchedRef.current = true;
    let cancelled = false;
    setDetectionsLoading(true);
    setDetectionsError(null);
    Promise.all([
      fetchInteriorDetections(jobId),
      fetchInteriorDetectionsImageObjectUrl(jobId).catch((err) => {
        // The backdrop is best-effort — boxes still render meaning even
        // if the image itself failed to load.
        log.error(`Failed to fetch detections image for job ${jobId}:`, err);
        return null;
      }),
    ])
      .then(([det, url]) => {
        if (cancelled) {
          // Effect torn down before we committed — revoke the URL we
          // just created so it doesn't leak (nothing rendered it).
          if (url) URL.revokeObjectURL(url);
          return;
        }
        detectionsImageUrlRef.current = url;
        setDetectionsData(det);
        setDetectionsImageUrl(url);
      })
      .catch((err) => {
        if (!cancelled) {
          log.error(`Failed to fetch detections for job ${jobId}:`, err);
          setDetectionsError(err instanceof Error ? err.message : 'Failed to load detections');
        }
      })
      .finally(() => {
        if (!cancelled) setDetectionsLoading(false);
      });
    // Intentionally does NOT revoke the committed object URL here: the
    // fetch is one-shot (ref-gated) and the cached <img> must survive
    // tab switches. Revocation happens on close / unmount below.
    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, tab]);

  // Metadata: lazy on first visit.
  useEffect(() => {
    if (!isOpen || !jobId || tab !== 'metadata') return;
    if (metadataFetchedRef.current) return;
    metadataFetchedRef.current = true;
    let cancelled = false;
    setMetadataLoading(true);
    setMetadataError(null);
    fetchInteriorDebugMetadata(jobId)
      .then((resp) => {
        if (!cancelled) setMetadataData(resp);
      })
      .catch((err) => {
        if (!cancelled) {
          log.error(`Failed to fetch metadata for job ${jobId}:`, err);
          setMetadataError(err instanceof Error ? err.message : 'Failed to load metadata');
        }
      })
      .finally(() => {
        if (!cancelled) setMetadataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, tab]);

  // Generation log: lazy on first visit.
  useEffect(() => {
    if (!isOpen || !jobId || tab !== 'log') return;
    if (logFetchedRef.current) return;
    logFetchedRef.current = true;
    let cancelled = false;
    setLogLoading(true);
    setLogError(null);
    fetchInteriorGenerationLog(jobId)
      .then((resp) => {
        if (!cancelled) setLogData(resp);
      })
      .catch((err) => {
        if (!cancelled) {
          log.error(`Failed to fetch generation log for job ${jobId}:`, err);
          setLogError(err instanceof Error ? err.message : 'Failed to load generation log');
        }
      })
      .finally(() => {
        if (!cancelled) setLogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, tab]);

  // Revoke the detections backdrop object URL once, on unmount.
  useEffect(() => {
    return () => {
      if (detectionsImageUrlRef.current) {
        URL.revokeObjectURL(detectionsImageUrlRef.current);
        detectionsImageUrlRef.current = null;
      }
    };
  }, []);

  // Reset transient state on close.
  useEffect(() => {
    if (!isOpen) {
      setTab('calls');
      if (detectionsImageUrlRef.current) {
        URL.revokeObjectURL(detectionsImageUrlRef.current);
        detectionsImageUrlRef.current = null;
      }
      setDetectionsData(null);
      setDetectionsImageUrl(null);
      setDetectionsError(null);
      setDetectionsLoading(false);
      detectionsFetchedRef.current = false;
      setCallsData(null);
      setCallsError(null);
      setCallsLoading(false);
      callsFetchedRef.current = false;
      setMetadataData(null);
      setMetadataError(null);
      setMetadataLoading(false);
      metadataFetchedRef.current = false;
      setLogData(null);
      setLogError(null);
      setLogLoading(false);
      logFetchedRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    // This modal renders *inside* the interior ImagesModal, whose root
    // div is itself the backdrop (`onClick={onClose}`). Stop click
    // propagation at our container so interacting with the debug panel
    // (tabs, buttons, backdrop) never bubbles up and tears down the
    // ImagesModal underneath. Mirrors the interior Lightbox pattern.
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-7xl w-[96vw] max-h-[92vh] overflow-hidden flex flex-col mx-3 sm:mx-4">
        {/* Header */}
        <div className="shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-b border-surface-200 flex items-center justify-between gap-2 flex-wrap bg-surface-50">
          <div className="flex items-center gap-3 min-w-0">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-surface-800">Interior Debug</h3>
              {scanId && <p className="text-xs font-mono text-surface-500 truncate">{scanId}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Tabs */}
            <div className="flex items-center bg-surface-100 rounded-lg p-0.5">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    tab === t.id ? 'bg-white text-surface-800 shadow-sm' : 'text-surface-500 hover:text-surface-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-surface-500 hover:text-surface-800 hover:bg-surface-100 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {tab === 'calls' && (
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {callsLoading && (
                <div className="p-12 text-center text-surface-500">Loading generation calls...</div>
              )}
              {callsError && !callsLoading && (
                <div className="p-12 text-center">
                  <p className="text-surface-700 font-medium">Failed to load generation calls</p>
                  <p className="text-sm text-surface-500 mt-1">{callsError}</p>
                </div>
              )}
              {callsData && !callsLoading && !callsError && <GenerationCallsTab data={callsData} />}
            </div>
          )}

          {tab === 'detections' && (
            <div className="flex-1 min-h-0 overflow-y-auto">
              {detectionsLoading && (
                <div className="p-12 text-center text-surface-500">Loading detections...</div>
              )}
              {detectionsError && !detectionsLoading && (
                <div className="p-12 text-center">
                  <p className="text-surface-700 font-medium">Failed to load detections</p>
                  <p className="text-sm text-surface-500 mt-1">{detectionsError}</p>
                </div>
              )}
              {detectionsData && !detectionsLoading && !detectionsError && (
                <DetectionsTab data={detectionsData} imageUrl={detectionsImageUrl} />
              )}
            </div>
          )}

          {tab === 'metadata' && (
            <div className="flex-1 min-h-0 overflow-y-auto">
              {metadataLoading && <div className="p-12 text-center text-surface-500">Loading metadata...</div>}
              {metadataError && !metadataLoading && (
                <div className="p-12 text-center">
                  <p className="text-surface-700 font-medium">Failed to load metadata</p>
                  <p className="text-sm text-surface-500 mt-1">{metadataError}</p>
                </div>
              )}
              {metadataData && !metadataLoading && !metadataError && <MetadataTab data={metadataData} />}
            </div>
          )}

          {tab === 'log' && (
            <div className="flex-1 min-h-0 overflow-auto">
              {logLoading && <div className="p-12 text-center text-surface-500">Loading generation log...</div>}
              {logError && !logLoading && (
                <div className="p-12 text-center">
                  <p className="text-surface-700 font-medium">Failed to load generation log</p>
                  <p className="text-sm text-surface-500 mt-1">{logError}</p>
                </div>
              )}
              {logData && !logLoading && !logError && <LogTab data={logData} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
